import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/headers/AdminHeader';
import { StatusConfirmationPopup } from '../../components/popups/AgencyDetailStopPop';
import AgencyInfoTab from '../../tabs/agencyDetail/AgencyInfoTab';
import AgencyInsuranceTab from '../../tabs/agencyDetail/AgencyInsuranceTab';
import AgencySolicitorsTab from '../../tabs/agencyDetail/AgencySolicitorsTab';
import { fetchAgencyData } from '../../services/api/AgencyService';
import { fetchInsuranceCompanies, fetchAgencyInsuranceCompanies } from '../../services/api/InsuranceService';
import { searchSolicitor, updateSolicitor } from '../../services/api/SolicitorService';
import '../../styles/Tabs.css';
import iconTitle from '../../assets/img/icon-square.svg';

// 代理店詳細ページコンポーネント
// ※pages/agencyにも同様のコンポーネントがあるため、修正時は同時に確認すること
const AgencyDetail = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const [agencyData, setAgencyData] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState({});
  const [agencyInsuranceCompanies, setAgencyInsuranceCompanies] = useState([]);
  const [solicitors, setSolicitors] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusConfirmationData, setStatusConfirmationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 初期データロード用のuseEffect
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (agencyId !== undefined && agencyId !== null && agencyId !== '') {
          // 代理店データを取得
          const agencyResponse = await fetchAgencyData(agencyId, { 
            showErrorDialog: false 
          });
          setAgencyData(agencyResponse);

          // 保険会社データを取得
          handleDataUpdated();

          // 募集人データを取得
          const searchData = {
            agency_id: parseInt(agencyId),
            target: null,
            search_word: null,
            is_active: null
          };
          const solicitorsResponse = await searchSolicitor(searchData);
          setSolicitors(solicitorsResponse);
        } else {
          setError('代理店IDが指定されていません。');
        }
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [agencyId]);

  const handleDataUpdated = async () => {
    try {
      // 保険会社データを取得
      const insuranceCompaniesResponse = await fetchInsuranceCompanies();

      // 代理店と保険会社の紐づけデータを取得
      const agencyInsuranceCompaniesResponse = await fetchAgencyInsuranceCompanies(agencyId);
      setAgencyInsuranceCompanies(agencyInsuranceCompaniesResponse);

      // 代理店に紐づいている保険会社のIDリストを作成
      const agencyCompanyIds = agencyInsuranceCompaniesResponse.map(company => company.id);
      
      // 紐づいている保険会社のみをフィルタリング
      const filteredInsuranceCompanies = insuranceCompaniesResponse.filter(company => 
        agencyCompanyIds.includes(company.id)
      );
      setInsuranceCompanies(filteredInsuranceCompanies);
    } catch (error) {
      console.error('データ更新エラー:', error);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/agency/edit/${agencyId}`);
  };

  // 停止/再開ボタンのクリックハンドラー
  const handleStatusButtonClick = (solicitor) => {
    // 確認ポップアップ用のデータを設定
    setStatusConfirmationData(solicitor);
    setShowStatusConfirmation(true);
  };
  
  // 確認ポップアップを閉じる
  const closeStatusConfirmation = () => {
    setShowStatusConfirmation(false);
    setStatusConfirmationData(null);
  };
  
  // 確認後に実際にステータスを変更する処理
  const confirmStatusChange = () => {
    if (statusConfirmationData) {
      handleStatusToggle(statusConfirmationData.id);
    }
    closeStatusConfirmation();
  };
  
  // ステータス変更ハンドラ - 停止/再開ボタンの処理
  const handleStatusToggle = async (solicitorId) => {
    setIsLoading(true);

    try {
      // 対象の募集人を特定
      const targetSolicitor = solicitors.find(solicitor => solicitor.id === solicitorId);
      
      if (!targetSolicitor) {
        throw new Error('募集人が見つかりません');
      }
      
      // ステータスを反転させる
      const newStatus = !targetSolicitor.is_active;
      
      // APIを呼び出してサーバー側のデータを更新
      await updateSolicitor(solicitorId, { 
        is_active: newStatus 
      });
      
      // 成功したら、ローカルのステートも更新
      const updatedSolicitors = solicitors.map(solicitor => {
        if (solicitor.id === solicitorId) {
          return { ...solicitor, is_active: newStatus };
        }
        return solicitor;
      });
      
      setSolicitors(updatedSolicitors);
      
    } catch (error) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // エラー発生、またはデータがない場合
  if (error || !agencyData) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate(-1)}>前の画面に戻る</button>
      </div>
    );
  }

  return (
    <>
      <AdminHeader />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>代理店詳細：{agencyData.name}</h1>
            </div>
          </div>
          <div className="tab-container">
            <div className="tab-menu">
              <button 
                className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                onClick={() => setActiveTab('info')}
              >
                代理店情報
              </button>
              <button 
                className={`tab-button ${activeTab === 'insurance' ? 'active' : ''}`}
                onClick={() => setActiveTab('insurance')}
              >
                登録保険会社
              </button>
              <button 
                className={`tab-button ${activeTab === 'solicitors' ? 'active' : ''}`}
                onClick={() => setActiveTab('solicitors')}
              >
                募集人
              </button>
            </div>
            <div className="tab-content">
              {/* 代理店情報タブ */}
              <div className={`tab-panel ${activeTab === 'info' ? 'active' : ''}`}>
                {activeTab === 'info' && (
                  <AgencyInfoTab 
                    agencyData={agencyData}
                    onEdit={handleEdit}
                    onReturn={() => navigate(-1)}
                  />
                )}
              </div>
              {/* 登録保険会社タブ */}
              <div className={`tab-panel ${activeTab === 'insurance' ? 'active' : ''}`}>
                {activeTab === 'insurance' && (
                  <AgencyInsuranceTab 
                    insuranceCompanies={insuranceCompanies}
                    onEdit={handleEdit}
                    onReturn={() => navigate(-1)}
                    onDataUpdated={handleDataUpdated}
                  />
                )}
              </div>
              {/* 募集人タブ */}
              <div className={`tab-panel ${activeTab === 'solicitors' ? 'active' : ''}`}>
                {activeTab === 'solicitors' && (
                  <AgencySolicitorsTab 
                    agencyId={agencyId}
                    solicitors={solicitors}
                    onStatusChange={handleStatusButtonClick}
                    onReturn={() => navigate(-1)}
                    onAddSolicitor={() => navigate(`/admin/agency/registration/${agencyId}`)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 停止/再開確認ポップアップ */}
      <StatusConfirmationPopup
        showConfirmationPopup={showStatusConfirmation}
        confirmationPopupData={statusConfirmationData}
        closeConfirmationPopup={closeStatusConfirmation}
        confirmStatusChange={confirmStatusChange}
      />
    </>
  );
};

export default AgencyDetail;